/**
 * TS mirror of infrastructure/lambdaFunctions/sharedModules/python/sales_code_routing.py.
 *
 * Six CardConnect portfolios (Michael's prod table). Each pairs a sales code
 * with its own Application Template ID, split by whether the merchant's
 * pricing is above or at/under the 4bp + 4c threshold, and by salesperson
 * attribution (Landon / Houston / generic Wilma). Two newer Wilma codes
 * (NEWAI / NEWAI2) reuse the generic-Wilma templates and take the threshold
 * side of the template they share.
 *
 * Used by the NewApplication picker for the option list and the Auto default
 * hint. The authoritative selection (and the paired template) is resolved
 * backend-side in sales_code_routing; keep this in sync with it.
 *
 * Threshold: SSTHUB 10 sits exactly on 4bp + 4c at $700K/mo, so volume
 * >= $700K is at/under threshold (WILMA-AI2 side), below is above threshold
 * (WILMA-AI side).
 */

export interface Portfolio {
  salesCode: string;
  templateId: string;
  aboveThreshold: boolean;
  attribution: string;
}

export const PORTFOLIOS: Portfolio[] = [
  { salesCode: "CCIP-SSTHUB1", templateId: "275115", aboveThreshold: true, attribution: "Landon" },
  { salesCode: "CCIP-SSTHUB2", templateId: "275114", aboveThreshold: true, attribution: "Houston" },
  { salesCode: "CCIP-WILMA-AI", templateId: "275113", aboveThreshold: true, attribution: "generic Wilma" },
  { salesCode: "CCIP-SSTHUB3", templateId: "275112", aboveThreshold: false, attribution: "Houston" },
  { salesCode: "CCIP-SSTHUB4", templateId: "275111", aboveThreshold: false, attribution: "Landon" },
  { salesCode: "CCIP-WILMA-AI2", templateId: "274130", aboveThreshold: false, attribution: "generic Wilma" },
  // NEWAI shares WILMA-AI2's at/under template, NEWAI2 shares WILMA-AI's above
  // template (the numbering is crossed relative to AI/AI2).
  { salesCode: "CCIP-WILMA-NEWAI", templateId: "274130", aboveThreshold: false, attribution: "new Wilma" },
  { salesCode: "CCIP-WILMA-NEWAI2", templateId: "275113", aboveThreshold: true, attribution: "new Wilma" },
];

// Auto (threshold) defaults: the generic-Wilma portfolio for each side.
export const SALES_CODE_PRIMARY = "CCIP-WILMA-AI"; // above 4bp+4c
export const SALES_CODE_SECONDARY = "CCIP-WILMA-AI2"; // at/under 4bp+4c

// The newer Wilma codes. Never an Auto default; picked explicitly.
export const SALES_CODE_NEWAI = "CCIP-WILMA-NEWAI"; // at/under 4bp+4c
export const SALES_CODE_NEWAI2 = "CCIP-WILMA-NEWAI2"; // above 4bp+4c

export const WILMA_AI2_VOLUME_THRESHOLD = 700_000;

// The SSTHUB rate-schedule portfolio each side prices from. These strings
// match the `portfolioPrefix` on the rate templates (sstshubRateTemplates.ts)
// so a sales code can steer rate-schedule selection to its own side (option
// A). Mirrors sales_code_routing.PORTFOLIO_PREFIX_ABOVE/_AT_OR_UNDER.
export const PORTFOLIO_PREFIX_ABOVE = "WILMA-AI"; // above 4bp+4c
export const PORTFOLIO_PREFIX_AT_OR_UNDER = "WILMA-AI2"; // at/under 4bp+4c

// Dev-only template overrides. Mirrors sales_code_routing._DEV_TEMPLATE_
// OVERRIDES: in the dev environment the prod portfolio templates do not
// exist in the dev CoPilot account, so CCIP-WILMA-AI points at the shared
// dev/test template 52971 instead of its prod template. CCIP-WILMA-NEWAI2
// shares that prod template (275113), so it takes the same override. Prod is
// untouched.
const DEV_TEMPLATE_OVERRIDES: Record<string, string> = {
  "CCIP-WILMA-AI": "52971",
  "CCIP-WILMA-NEWAI2": "52971",
};

// The Wilma portfolios the picker offers by default: the two generic-Wilma
// codes the Auto threshold default resolves to, then the two newer Wilma
// codes. The four SSTHUB salesperson-attribution portfolios are opt-in (see
// salesCodeOptions({ includeAllPortfolios: true })).
export const WILMA_SALES_CODES = [
  SALES_CODE_PRIMARY,
  SALES_CODE_SECONDARY,
  SALES_CODE_NEWAI,
  SALES_CODE_NEWAI2,
] as const;

// Short, operator-friendly names for the picker (the raw code is shown
// alongside; the paired template is surfaced separately by the caller).
const SALES_CODE_LABELS: Record<string, string> = {
  [SALES_CODE_PRIMARY]: "Wilma AI",
  [SALES_CODE_SECONDARY]: "Wilma AI2",
  [SALES_CODE_NEWAI]: "Wilma NewAI",
  [SALES_CODE_NEWAI2]: "Wilma NewAI2",
};

export interface SalesCodeOption {
  value: string; // "" = Auto (backend routes by volume threshold)
  label: string;
}

// Option list for SalesCodeSelect: Auto first, then the four WILMA codes,
// and (opt-in) the four SSTHUB salesperson-attribution portfolios
// the backend also accepts (sales_code_routing.ALLOWED_SALES_CODES). The
// paired template is shown separately by the caller (and is dev-aware), so
// it is deliberately not baked into the label here.
export function salesCodeOptions(
  opts: { includeAllPortfolios?: boolean } = {},
): SalesCodeOption[] {
  const options: SalesCodeOption[] = [
    { value: "", label: "Auto — route by monthly volume" },
    ...WILMA_SALES_CODES.map((code) => ({
      value: code,
      label: `${SALES_CODE_LABELS[code]} — ${code}`,
    })),
  ];
  if (!opts.includeAllPortfolios) return options;
  const wilmaCodes: readonly string[] = WILMA_SALES_CODES;
  for (const p of PORTFOLIOS) {
    if (wilmaCodes.includes(p.salesCode)) continue;
    const side = p.aboveThreshold ? "above threshold" : "at/under threshold";
    options.push({
      value: p.salesCode,
      label: `${p.attribution} (${side}) — ${p.salesCode}`,
    });
  }
  return options;
}

/**
 * Application Template ID paired with `salesCode`, or null when the code is
 * not one of the known portfolios (e.g. "" = Auto, where the caller keeps
 * whatever template default it already had). Mirrors
 * sales_code_routing.template_id_for_sales_code, including the dev override.
 */
export function templateIdForSalesCode(
  salesCode: string,
  opts: { dev?: boolean } = {},
): string | null {
  if (opts.dev && DEV_TEMPLATE_OVERRIDES[salesCode]) {
    return DEV_TEMPLATE_OVERRIDES[salesCode];
  }
  const portfolio = PORTFOLIOS.find((p) => p.salesCode === salesCode);
  return portfolio ? portfolio.templateId : null;
}

/**
 * SSTHUB rate-schedule portfolio prefix for `salesCode`: "WILMA-AI" for an
 * above-threshold code, "WILMA-AI2" for an at/under code, null when the code
 * is blank or not one of the known portfolios (null = fall back to volume-
 * only rate-schedule selection). Mirrors
 * sales_code_routing.portfolio_prefix_for_sales_code. Lets the previewed rate
 * schedule follow the effective sales code's side (option A), matching what
 * boards backend-side. Does not affect template pairing.
 */
export function portfolioPrefixForSalesCode(
  salesCode: string | null | undefined,
): string | null {
  if (!salesCode) return null;
  const portfolio = PORTFOLIOS.find((p) => p.salesCode === salesCode);
  if (!portfolio) return null;
  return portfolio.aboveThreshold
    ? PORTFOLIO_PREFIX_ABOVE
    : PORTFOLIO_PREFIX_AT_OR_UNDER;
}

/**
 * Threshold default: the generic-Wilma code for the merchant's side. Returns
 * null when volume is missing or unparseable (form stores it as a string).
 */
export function defaultSalesCodeForVolume(
  monthlyVolume: number | string | null | undefined
): string | null {
  if (monthlyVolume === null || monthlyVolume === undefined || monthlyVolume === "") {
    return null;
  }
  const volume =
    typeof monthlyVolume === "number" ? monthlyVolume : Number(monthlyVolume);
  if (!Number.isFinite(volume)) return null;
  return volume >= WILMA_AI2_VOLUME_THRESHOLD
    ? SALES_CODE_SECONDARY
    : SALES_CODE_PRIMARY;
}
